import { Logger } from '../logger';

/**
 * Guarded wrappers around the Web Storage API.
 *
 * Storage throws rather than returning null in several environments we ship to:
 * private-browsing modes, quota exhaustion, and some of the TV webviews. A
 * persisted filter or an analytics fingerprint must never be the reason a page
 * fails to render, so reads fall back and writes fail silently — logged, never
 * thrown.
 *
 * Prefer these helpers over touching `localStorage` / `sessionStorage`
 * directly. Keys with app-wide meaning (and the rules for what survives a
 * storage reset) live in `appStorage.ts`; keys that only one feature cares
 * about stay in that feature's module and use these helpers to read and write.
 */
export interface SafeStorage {
  /** The stored string, or `null` when absent or unreadable. */
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
  /** Parsed JSON, or `fallback` when absent, unreadable, or malformed. */
  readJson<T>(key: string, fallback: T): T;
  writeJson(key: string, value: unknown): void;
  /** Every key currently held, or `[]` when the area is unavailable. */
  keys(): string[];
  clear(): void;
}

type StorageAreaName = 'localStorage' | 'sessionStorage';

const createSafeStorage = (areaName: StorageAreaName): SafeStorage => {
  const area = (): Storage | null => {
    try {
      return typeof window === 'undefined' ? null : window[areaName];
    } catch (error) {
      Logger.info(`safeStorage: "${areaName}" is unavailable`, error);
      return null;
    }
  };

  const read = (key: string): string | null => {
    try {
      return area()?.getItem(key) ?? null;
    } catch (error) {
      Logger.info(`safeStorage: unable to read "${key}"`, error);
      return null;
    }
  };

  const write = (key: string, value: string): void => {
    try {
      area()?.setItem(key, value);
    } catch (error) {
      Logger.info(`safeStorage: unable to persist "${key}"`, error);
    }
  };

  const remove = (key: string): void => {
    try {
      area()?.removeItem(key);
    } catch (error) {
      Logger.info(`safeStorage: unable to remove "${key}"`, error);
    }
  };

  return {
    read,
    write,
    remove,

    readJson<T>(key: string, fallback: T): T {
      const raw = read(key);
      if (raw === null) return fallback;
      try {
        return JSON.parse(raw) as T;
      } catch (error) {
        // A malformed entry is unrecoverable, so drop it rather than hand the
        // same parse error back on every subsequent read.
        Logger.info(
          `safeStorage: discarding malformed JSON at "${key}"`,
          error
        );
        remove(key);
        return fallback;
      }
    },

    writeJson(key: string, value: unknown): void {
      try {
        write(key, JSON.stringify(value));
      } catch (error) {
        Logger.info(`safeStorage: unable to serialise "${key}"`, error);
      }
    },

    keys(): string[] {
      try {
        const storage = area();
        return storage ? Object.keys(storage) : [];
      } catch (error) {
        Logger.info(`safeStorage: unable to enumerate "${areaName}"`, error);
        return [];
      }
    },

    clear(): void {
      try {
        area()?.clear();
      } catch (error) {
        Logger.info(`safeStorage: unable to clear "${areaName}"`, error);
      }
    }
  };
};

export const localStore = createSafeStorage('localStorage');

export const sessionStore = createSafeStorage('sessionStorage');
